import React, { useState } from "react";
import { Mail, EyeOff } from "lucide-react";

/**
 * Click-to-reveal email component — reduces harvesting by naive spam bots that
 * regex-scrape the raw HTML. The email address never appears in the initial
 * DOM: it is assembled from `user` + `domain` only after the user clicks.
 *
 * Props:
 *  - user: local part (e.g. "service")
 *  - domain: domain part (e.g. "pttnz.net")
 *  - className: extra classes on the outer element
 *  - buttonLabel: label for the reveal button (default: "Click to reveal email")
 *  - subject: optional subject line for the mailto link
 */
const ObfuscatedEmail = ({
  user,
  domain,
  className = "",
  buttonLabel = "Click to reveal email",
  subject,
  testId,
}) => {
  const [revealed, setRevealed] = useState(false);

  if (!revealed) {
    return (
      <button
        type="button"
        onClick={() => setRevealed(true)}
        data-testid={testId ? `${testId}-reveal` : "email-reveal"}
        aria-label="Reveal email address"
        className={`inline-flex items-center gap-1.5 group cursor-pointer hover:text-[var(--ptt-teal)] transition-colors ${className}`}
      >
        <EyeOff size={13} className="opacity-70 group-hover:opacity-100 transition-opacity" />
        <span className="border-b border-dashed border-current/40 group-hover:border-current">{buttonLabel}</span>
      </button>
    );
  }

  // Assemble address only after reveal (never sits in the initial DOM)
  const address = `${user}\u0040${domain}`;
  const href = `mailto:${address}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`;

  return (
    <a
      href={href}
      data-testid={testId || "email-link"}
      className={`inline-flex items-center gap-1.5 hover:text-[var(--ptt-teal)] transition-colors ${className}`}
    >
      <Mail size={13} className="opacity-80" />
      <span>{address}</span>
    </a>
  );
};

export default ObfuscatedEmail;
